import type { ReactNode } from 'react';

import type { CellProps } from '../types';

/**
 * Cell component for defining custom cell rendering.
 *
 * This is a declarative/config component that returns null.
 * The cell renderer is extracted by parseChildren and used
 * by TableBody to render cells.
 *
 * @example Pre-built cell type
 * ```tsx
 * <DataTable.Column id="createdAt" accessor="createdAt">
 *   <DataTable.Header>Created</DataTable.Header>
 *   <DataTable.Cell type="date" />
 * </DataTable.Column>
 * ```
 *
 * @example Custom render function
 * ```tsx
 * <DataTable.Column id="status" accessor="status">
 *   <DataTable.Header>Status</DataTable.Header>
 *   <DataTable.Cell>
 *     {({ value, row }) => (
 *       <StatusBadge status={value} id={row.original.id} />
 *     )}
 *   </DataTable.Cell>
 * </DataTable.Column>
 * ```
 */
export const Cell = <TData, TValue = unknown>(_props: CellProps<TData, TValue>): ReactNode => {
  // Pure declarative component - renderer is extracted by parseChildren
  return null;
};

Cell.displayName = 'DataTable.Cell';
